import { useTranslations } from '@vocab/react';
import type { RefineField } from '../RefineBar';
import translations from './.vocab';
import WorkTypeSummary from '../WorkTypeSummary/WorkTypeSummary';
import { useSelector } from 'react-redux';
import WorkTypes from './WorkTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Option } from 'src/types/globals';
import {
  selectLanguage,
  selectSearchQuery,
  selectZone,
} from 'src/store/selectors';
import { getWorkTypes } from 'src/modules/refine-job-search';

export const useWorkTypesField = (
  handleSelection: (fieldId: string) => void,
): RefineField => {
  const { t } = useTranslations(translations);

  const zone = useSelector(selectZone);
  const language = useSelector(selectLanguage);
  const query = useSelector(selectSearchQuery);
  const workTypes = query?.worktype || '';

  const workTypeList = getWorkTypes(zone, language);

  const [activeValue, setActiveValue] = useState<string[]>(
    workTypes ? workTypes.split(',') : [],
  );

  useEffect(() => {
    if (workTypes !== activeValue.join(',')) {
      setActiveValue(workTypes === '' ? [] : workTypes.split(','));
    }
  }, [workTypes, activeValue]);

  const getOptionItem = useCallback(
    (label: string, value: string) => {
      const isActive = activeValue.includes(value);
      const workTypeRefineParam = isActive
        ? activeValue.filter((v) => v !== value)
        : [...activeValue, value];
      return {
        label,
        value,
        isActive,
        refineParams: {
          ...query,
          worktype: workTypeRefineParam.join(','),
        },
      };
    },
    [activeValue, query],
  );

  const workTypeOptions: Option[] = useMemo(
    () =>
      workTypeList
        ? workTypeList.map(({ label, value }) => getOptionItem(label, value))
        : [],
    [workTypeList, getOptionItem],
  );

  const handleWorkTypeSelection = (value: string) => {
    setActiveValue(() => {
      if (activeValue.includes(value)) {
        return activeValue.filter((v) => v !== value);
      }
      return [...activeValue, value];
    });
    handleSelection('WorkType');
  };

  return {
    id: 'WorkType',
    ariaLabel: t('work type'),
    label: <WorkTypeSummary workTypes={workTypeOptions} />,
    filterComponent: () => (
      <WorkTypes
        handleSelection={handleWorkTypeSelection}
        workTypes={workTypeOptions}
      />
    ),
  };
};
